import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, Search, Download, Award, GraduationCap, Users, 
  AlertTriangle, CheckCircle, Clock, History, UserCheck
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CertificatesPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [passoutHistory, setPassoutHistory] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [tcDialog, setTcDialog] = useState(false);
  const [passoutDialog, setPassoutDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Forms
  const [tcForm, setTcForm] = useState({ reason: 'Parent Request', remarks: '' });
  const [passoutForm, setPassoutForm] = useState({ passout_year: new Date().getFullYear().toString(), final_result: 'Pass', remarks: '' });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/certificates/students-by-status?status=${statusFilter}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/certificates/passout-history`, { headers: getAuthHeader() })
      ]);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
      setPassoutHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const issueTC = async () => {
    if (!selectedStudent) return;
    try {
      const res = await axios.post(`${API_URL}/api/certificates/transfer-certificate/${selectedStudent.id}`, null, {
        headers: getAuthHeader(),
        params: tcForm
      });
      toast.success(`TC Issued: ${res.data.tc_number}`);
      setTcDialog(false);
      setTcForm({ reason: 'Parent Request', remarks: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue TC');
    }
  };

  const markPassout = async () => {
    if (!selectedStudent) return;
    try {
      await axios.post(`${API_URL}/api/certificates/mark-passout/${selectedStudent.id}`, null, {
        headers: getAuthHeader(),
        params: passoutForm
      });
      toast.success('Student marked as passout');
      setPassoutDialog(false);
      setPassoutForm({ passout_year: new Date().getFullYear().toString(), final_result: 'Pass', remarks: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark passout');
    }
  };

  const downloadTC = async (studentId) => {
    try {
      const response = await axios.get(`${API_URL}/api/certificates/transfer-certificate/${studentId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TC_${studentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('TC downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download TC');
    }
  };

  const downloadCharacterCertificate = async (studentId) => {
    try {
      const response = await axios.get(`${API_URL}/api/certificates/character-certificate/${studentId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Character_Certificate_${studentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Character Certificate downloaded');
    } catch (error) {
      toast.error('Failed to download Character Certificate');
    }
  };

  const openTcDialog = (student) => {
    setSelectedStudent(student);
    setTcDialog(true);
  };

  const openPassoutDialog = (student) => {
    setSelectedStudent(student);
    setPassoutDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'passout':
        return <Badge className="bg-blue-500">Passout</Badge>;
      case 'tc_issued':
        return <Badge className="bg-amber-500">TC Issued</Badge>;
      case 'left':
        return <Badge variant="destructive">Left</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Suspended</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Active'}</Badge>;
    }
  };

  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.student_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = [
    { title: 'Active Students', value: students.filter(s => !s.status || s.status === 'active').length, icon: Users, color: 'bg-green-500' },
    { title: 'Passout', value: passoutHistory.filter(h => h.certificate_type === 'passout').length, icon: GraduationCap, color: 'bg-blue-500' },
    { title: 'TC Issued', value: passoutHistory.filter(h => h.certificate_type === 'transfer_certificate').length, icon: FileText, color: 'bg-amber-500' },
  ];

  return (
    <DashboardLayout title="Certificates & Passout">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="history">Passout History</TabsTrigger>
        </TabsList>

        {/* Students Tab */}
        <TabsContent value="students" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                      <div className={`${stat.color} p-2 rounded-lg`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold">{stat.value}</div></CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-students"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Students</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="passout">Passout</SelectItem>
                <SelectItem value="tc_issued">TC Issued</SelectItem>
                <SelectItem value="left">Left</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Students Table */}
          <Card>
            <CardHeader>
              <CardTitle>Students ({filteredStudents.length})</CardTitle>
              <CardDescription>Manage student certificates and passout status</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left text-sm font-medium">Student ID</th>
                      <th className="p-3 text-left text-sm font-medium">Name</th>
                      <th className="p-3 text-left text-sm font-medium">Class</th>
                      <th className="p-3 text-left text-sm font-medium">Status</th>
                      <th className="p-3 text-left text-sm font-medium">TC Number</th>
                      <th className="p-3 text-left text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(student => (
                      <tr key={student.id} className="border-t" data-testid={`student-row-${student.id}`}>
                        <td className="p-3 font-mono text-sm">{student.student_id}</td>
                        <td className="p-3 font-medium">{student.name}</td>
                        <td className="p-3 text-sm">{student.class_name || '-'}</td>
                        <td className="p-3">{getStatusBadge(student.status)}</td>
                        <td className="p-3 font-mono text-sm">{student.tc_number || '-'}</td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            {/* Character Certificate - available for all */}
                            <Button size="sm" variant="outline" onClick={() => downloadCharacterCertificate(student.id)} data-testid={`download-cc-${student.id}`}>
                              <Award className="h-4 w-4 mr-1" /> CC
                            </Button>
                            
                            {/* If TC issued - show download TC */}
                            {student.tc_number && (
                              <Button size="sm" variant="outline" onClick={() => downloadTC(student.id)} data-testid={`download-tc-${student.id}`}>
                                <Download className="h-4 w-4 mr-1" /> TC
                              </Button>
                            )}
                            
                            {/* If active - show Issue TC and Mark Passout */}
                            {(!student.status || student.status === 'active') && (
                              <>
                                <Button size="sm" variant="secondary" onClick={() => openTcDialog(student)} data-testid={`issue-tc-${student.id}`}>
                                  <FileText className="h-4 w-4 mr-1" /> Issue TC
                                </Button>
                                <Button size="sm" variant="default" onClick={() => openPassoutDialog(student)} data-testid={`mark-passout-${student.id}`}>
                                  <GraduationCap className="h-4 w-4 mr-1" /> Passout
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          No students found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Passout History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" /> Passout & TC History
              </CardTitle>
              <CardDescription>Record of all passout students and issued transfer certificates</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left text-sm font-medium">Date</th>
                      <th className="p-3 text-left text-sm font-medium">Student</th>
                      <th className="p-3 text-left text-sm font-medium">Class</th>
                      <th className="p-3 text-left text-sm font-medium">Type</th>
                      <th className="p-3 text-left text-sm font-medium">Certificate No.</th>
                      <th className="p-3 text-left text-sm font-medium">Reason/Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passoutHistory.map(record => (
                      <tr key={record.id} className="border-t">
                        <td className="p-3 text-sm">{record.issued_date}</td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{record.student_name}</p>
                            <p className="text-xs text-muted-foreground">{record.student_roll}</p>
                          </div>
                        </td>
                        <td className="p-3 text-sm">{record.class_name}</td>
                        <td className="p-3">
                          <Badge variant={record.certificate_type === 'transfer_certificate' ? 'secondary' : 'default'}>
                            {record.certificate_type === 'transfer_certificate' ? 'TC' : 'Passout'}
                          </Badge>
                        </td>
                        <td className="p-3 font-mono text-sm">{record.certificate_number || record.passout_year || '-'}</td>
                        <td className="p-3 text-sm">{record.reason || record.final_result || '-'}</td>
                      </tr>
                    ))}
                    {passoutHistory.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          No passout/TC history found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Issue TC Dialog */}
      <Dialog open={tcDialog} onOpenChange={setTcDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Transfer Certificate</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedStudent.name}</p>
                <p className="text-sm text-muted-foreground">{selectedStudent.student_id} • {selectedStudent.class_name}</p>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-400">Warning</p>
                    <p className="text-amber-700 dark:text-amber-500">Once a TC is issued, the student's status will be changed to "TC Issued" and cannot be reverted.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label>Reason for Leaving</Label>
                <Select value={tcForm.reason} onValueChange={v => setTcForm({...tcForm, reason: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Parent Request">Parent Request</SelectItem>
                    <SelectItem value="Transfer to Another School">Transfer to Another School</SelectItem>
                    <SelectItem value="Migration">Migration</SelectItem>
                    <SelectItem value="Personal Reasons">Personal Reasons</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Remarks (Optional)</Label>
                <Textarea 
                  value={tcForm.remarks} 
                  onChange={e => setTcForm({...tcForm, remarks: e.target.value})}
                  placeholder="Any additional remarks..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTcDialog(false)}>Cancel</Button>
            <Button onClick={issueTC} data-testid="confirm-issue-tc">Issue TC</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Passout Dialog */}
      <Dialog open={passoutDialog} onOpenChange={setPassoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Student as Passout</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedStudent.name}</p>
                <p className="text-sm text-muted-foreground">{selectedStudent.student_id} • {selectedStudent.class_name}</p>
              </div>
              <div>
                <Label>Passout Year</Label>
                <Input 
                  value={passoutForm.passout_year} 
                  onChange={e => setPassoutForm({...passoutForm, passout_year: e.target.value})}
                  placeholder="e.g., 2026"
                />
              </div>
              <div>
                <Label>Final Result</Label>
                <Select value={passoutForm.final_result} onValueChange={v => setPassoutForm({...passoutForm, final_result: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass">Pass</SelectItem>
                    <SelectItem value="Fail">Fail</SelectItem>
                    <SelectItem value="Distinction">Distinction</SelectItem>
                    <SelectItem value="First Class">First Class</SelectItem>
                    <SelectItem value="Second Class">Second Class</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Remarks (Optional)</Label>
                <Textarea 
                  value={passoutForm.remarks} 
                  onChange={e => setPassoutForm({...passoutForm, remarks: e.target.value})}
                  placeholder="Any additional remarks..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPassoutDialog(false)}>Cancel</Button>
            <Button onClick={markPassout} data-testid="confirm-mark-passout">
              <GraduationCap className="h-4 w-4 mr-2" /> Mark as Passout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CertificatesPage;
